import { useCallback, useMemo } from 'react'
import { useMap } from 'react-map-gl/maplibre'
import type { Map as MapLibreMap, LngLatBoundsLike } from 'maplibre-gl'

/**
 * Options for camera animations (flyTo, easeTo)
 */
interface CameraOptions {
  center?: [number, number]
  zoom?: number
  bearing?: number
  pitch?: number
  duration?: number
  essential?: boolean
}

/**
 * Options for fitBounds
 */
interface FitBoundsOptions {
  padding?:
    | number
    | { top: number; bottom: number; left: number; right: number }
  maxZoom?: number
  duration?: number
}

/**
 * Options for queryRenderedFeatures
 * Using a permissive type to avoid strict MapLibre filter type constraints
 */
type QueryFeaturesOptions = {
  layers?: string[]
  filter?: unknown
  validate?: boolean
}

/**
 * Return type for the useMapInstance hook
 */
interface UseMapInstanceReturn {
  /** The MapLibre GL map instance, or null if not available */
  map: MapLibreMap | null
  /** Whether the map instance is ready for use */
  isReady: boolean
  /** Animate the camera to a new position with a flight animation */
  flyTo: (options: CameraOptions) => void
  /** Animate the camera to a new position with easing */
  easeTo: (options: CameraOptions) => void
  /** Get the current map center as [lng, lat] */
  getCenter: () => [number, number] | null
  /** Get the current zoom level */
  getZoom: () => number | null
  /** Get the current visible bounds */
  getBounds: () => LngLatBoundsLike | null
  /** Query features rendered on the map */
  queryRenderedFeatures: (
    point?: [number, number] | [[number, number], [number, number]],
    options?: QueryFeaturesOptions
  ) => GeoJSON.Feature[] | []
}

/**
 * Adapter hook for accessing the MapLibre GL map instance.
 *
 * This hook provides a unified interface for accessing the map instance with:
 * 1. Test injection support via mapOverride parameter
 * 2. react-map-gl MapProvider context (production)
 *
 * @param mapOverride - Optional map instance for test injection
 * @returns Object containing map instance and bound methods
 *
 * @example
 * // Production usage
 * const { map, isReady, flyTo } = useMapInstance();
 * if (isReady) {
 *   flyTo({ center: [-122.4, 37.8], zoom: 12 });
 * }
 *
 * @example
 * // Test usage
 * const mockMap = createMockMap();
 * const { map, flyTo } = useMapInstance(mockMap);
 * flyTo({ center: [0, 0] });
 * expect(mockMap.flyTo).toHaveBeenCalled();
 */
const useMapInstance = (
  mapOverride?: MapLibreMap | null
): UseMapInstanceReturn => {
  // Get map from react-map-gl MapProvider context
  // The map is registered with id="mapGL" in Map.jsx
  const { mapGL } = useMap()

  // Resolve the map instance with priority:
  // 1. Test override (for unit tests)
  // 2. react-map-gl context (production)
  const map = useMemo((): MapLibreMap | null => {
    // Priority 1: Test injection
    if (mapOverride) {
      return mapOverride
    }

    // Priority 2: react-map-gl MapProvider context
    // mapGL is a MapRef object, we need to call getMap() to get the actual instance
    if (mapGL) {
      const mapInstance = mapGL.getMap?.()
      if (mapInstance) {
        return mapInstance
      }
    }

    return null
  }, [mapOverride, mapGL])

  const isReady = map !== null

  // Bound methods that read directly from map instance
  // These do NOT cache values in state - they read fresh from the map

  const flyTo = useCallback(
    (options: CameraOptions): void => {
      if (!map) return
      map.flyTo(options)
    },
    [map]
  )

  const easeTo = useCallback(
    (options: CameraOptions): void => {
      if (!map) return
      map.easeTo(options)
    },
    [map]
  )

  const getCenter = useCallback((): [number, number] | null => {
    if (!map) return null
    const center = map.getCenter()
    return [center.lng, center.lat]
  }, [map])

  const getZoom = useCallback((): number | null => {
    if (!map) return null
    return map.getZoom()
  }, [map])

  const getBounds = useCallback((): LngLatBoundsLike | null => {
    if (!map) return null
    return map.getBounds()
  }, [map])

  const queryRenderedFeatures = useCallback(
    (
      point?: [number, number] | [[number, number], [number, number]],
      options?: QueryFeaturesOptions
    ): GeoJSON.Feature[] => {
      if (!map) return []
      // Cast options to satisfy MapLibre's strict filter typing
      return map.queryRenderedFeatures(
        point,
        options as Parameters<MapLibreMap['queryRenderedFeatures']>[1]
      ) as GeoJSON.Feature[]
    },
    [map]
  )

  return {
    map,
    isReady,
    flyTo,
    easeTo,
    getCenter,
    getZoom,
    getBounds,
    queryRenderedFeatures,
  }
}

export default useMapInstance
export type {
  UseMapInstanceReturn,
  CameraOptions,
  FitBoundsOptions,
  QueryFeaturesOptions,
}
